
import { Component, Vue } from "vue-property-decorator";
import VueFilters from "@/vue-filters";
import { Action, Getter } from "vuex-class";
import IconTrash from "@/components/common/icon/IconTrash.vue";
import IconDownload from "@/components/common/icon/IconDownload.vue";
import IconReport from "../common/icon/IconReports.vue";
import Pagination from "@/components/common/Pagination.vue";
import { EReportType, IReportRequest } from "@/services/backend/report/report.types.js";
import { BvTableFieldArray } from "bootstrap-vue";
import FilterReports from "@/components/reports/FilterReports.vue";
import { Dictionary } from "vue-router/types/router";
import { ICountry, IReport, IUser } from "@/entities";
import { EDirection, EUserType, EReportStatus, IPageResponse } from "@/services";
import { EventBus } from "@/event-bus";
import { AxiosResponse } from "axios";
import { SweetAlertResult } from "sweetalert2";
import { i18n } from "@/i18n";

@Component({
  name: "reports-table",
  mixins: [VueFilters],
  components: {
    IconTrash,
    IconDownload,
    IconReport,
    Pagination,
    FilterReports,
  },
})
export default class ReportsTable extends Vue {
  @Action("fetchReportsPage") fetchReportsPage!: (
    params: IReportRequest
  ) => Promise<IPageResponse<IReport> | undefined>;

  @Action("fetchDeleteReport") fetchDeleteReport!: (params: number) => Promise<void>;

  @Action("fetchDownloadReport") fetchDownloadReport!: (
    params: number
  ) => Promise<AxiosResponse<Blob> | undefined>;

  @Getter("getLoggedUser") loggedUser: IUser | undefined;
  @Getter("getCountriesPage") countriesPage: IPageResponse<ICountry> | undefined;
  pageSize = 10;
  totalElements = 0;
  public reports: IPageResponse<IReport> | undefined = {
    content: [] as IReport[],
  } as IPageResponse<IReport>;
  private queryParams = this.$route.query;
  pageNumber = Number(this.queryParams.page) || 1;
  orderBy = this.queryParams.orderBy?.toString() || "id";
  sortDesc = this.queryParams.direction !== "ASC";
  public serviceParams: IReportRequest = {
    status: this.getQueryParamsReportStatus(this.queryParams),
    requestDate: this.queryParams.requestDate
      ? new Date(this.queryParams.requestDate.toString())
      : undefined,
    reportDate: this.queryParams.reportDate
      ? new Date(this.queryParams.reportDate.toString())
      : undefined,
    usernamePrecise: this.queryParams.usernamePrecise?.toString() === "true" || false,
    username: this.queryParams.username?.toString(),
    userType: this.getQueryParamsUserType(this.queryParams),
    countryId: this.queryParams.countryId ? Number(this.queryParams.countryId) : undefined,
    reportType: this.queryParams.reportType?.toString() as EReportType,
  };

  constructor() {
    super();
  }

  get fields(): BvTableFieldArray {
    return [
      {
        key: "actions-left",
        label: "",
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-first align-middle",
      },
      {
        key: "user.country.code",
        label: this.$t("column.country") as string,
        sortable: true,
        thClass: this.$ability.can("read", "AllReports") ? "table-header" : "table-header d-none",
        tdClass: this.$ability.can("read", "AllReports")
          ? "table-cell-middle"
          : "table-cell-middle d-none",
        formatter: (value: string | undefined) => this.$t(`country.${value?.toLowerCase()}`),
      },
      {
        key: "user.username",
        label: this.$t("label.iduser") as string,
        sortable: true,
        thClass: this.$ability.can("read", "AllReports") ? "table-header" : "table-header d-none",
        tdClass: this.$ability.can("read", "AllReports")
          ? "table-cell-middle"
          : "table-cell-middle d-none",
      },
      {
        key: "type",
        label: this.$t("column.report.type") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
        formatter: (value?: string) => (value ? this.$t(`option.type.${value.toLowerCase()}`) : ""),
      },
      {
        key: "filename",
        label: this.$t("column.filename") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
      },
      {
        key: "status",
        label: this.$t("column.status") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
        formatter: (value?: string) => (value ? this.$t(`option.type.${value.toLowerCase()}`) : ""),
      },
      {
        key: "requestDate",
        label: this.$t("column.request_date") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
        formatter: (value?: string) => `${this.filterDate(value)} ${this.filterTime(value)}`,
      },
      {
        key: "reportDate",
        label: this.$t("column.creation_date") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
        formatter: (value?: string) => `${this.filterDate(value)} ${this.filterTime(value)}`,
      },
      {
        key: "expirationDate",
        label: this.$t("column.expiration_date") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle",
        formatter: (value?: string) => `${this.filterDate(value)} ${this.filterTime(value)}`,
      },
      {
        key: "actions-right",
        label: "",
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-last",
      },
    ];
  }

  mounted(): void {
    this.getReports();
    EventBus.$on("update-reports-table", () => {
      this.onChangeCurrentPage(1);
    });
  }

  async onChangeCurrentPage(currentPage: number): Promise<void> {
    this.pageNumber = currentPage;
    await this.getReports();
  }

  async filterReports(): Promise<void> {
    this.$nextTick(async () => this.getReports());
  }

  getQueryParamsUserType(
    queryParams: Dictionary<string | (string | null)[]>
  ): EUserType | undefined {
    if (queryParams.userType === "CLIENTE") {
      return EUserType.CLIENTE;
    }

    return queryParams.userType === "PROSEGUR" ? EUserType.PROSEGUR : undefined;
  }

  getQueryParamsReportStatus(
    queryParams: Dictionary<string | (string | null)[]>
  ): EReportStatus | undefined {
    return Object.values(EReportStatus).find((s) => s === queryParams.status);
  }

  async getReports(): Promise<void> {
    this.serviceParams.size = this.pageSize;
    this.serviceParams.orderBy = this.orderBy || undefined;
    this.serviceParams.direction = this.sortDesc ? EDirection.DESC : EDirection.ASC;
    this.serviceParams.page = this.pageNumber;
    this.serviceParams.requestDate = (this.serviceParams.requestDate as Date)?.toISOString();
    this.serviceParams.reportDate = (this.serviceParams.reportDate as Date)?.toISOString();
    await this.$router
      .replace({ path: this.$route.path, query: this.serviceParams as Dictionary<string> })
      .catch(() => {
        // do nothing.
      });
    this.serviceParams.page = this.pageNumber - 1;
    this.reports = await this.fetchReportsPage(this.serviceParams);
    this.totalElements = this.reports?.totalElements || 0;
  }

  async downloadReport(id: number): Promise<void> {
    const response = await this.fetchDownloadReport(id);

    if (response) {
      const blobObj = response.data;
      const url = window.URL.createObjectURL(blobObj);
      const link = document.createElement("a");
      link.href = url;
      let filename = "";
      const disposition = response.headers["content-disposition"];
      if (disposition && disposition.indexOf("attachment") !== -1) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(disposition);
        if (matches != null && matches[1]) filename = matches[1].replace(/['"]/g, "");
      }
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
    } else {
      this.makeToast("danger", this.$t("download.error").toString());
    }
  }

  async removeReport(id: number): Promise<void> {
    const { isConfirmed } = await this.modalConfirmRemove();
    if (isConfirmed) {
      await this.fetchDeleteReport(id);
      await this.getReports();
      this.makeToast("success", this.$t("report.removed").toString());
    }
  }

  async modalConfirmRemove(): Promise<SweetAlertResult> {
    return this.$swal({
      title: i18n.t("message.generic.confirm.title").toString(),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: i18n.t("label.yes").toString(),
      cancelButtonText: i18n.t("label.no").toString(),
    });
  }

  makeToast(variant: string, message: string): void {
    this.$bvToast.toast(message, {
      autoHideDelay: 4000,
      variant,
      toaster: "b-toaster-bottom-center",
      noCloseButton: true,
    });
  }
}
